import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * Strip [Category]: ... [Title]: ... and dash block so only content snippet is shown.
 */
function getSnippetOnly(text) {
    if (!text || typeof text !== "string") return text || "";
    let s = text;
    const dashBlock = /-{5,}/;
    const dashIdx = s.search(dashBlock);
    if (dashIdx !== -1) {
        s = s.slice(dashIdx).replace(dashBlock, "").trim();
    } else {
        s = s.replace(/^\s*\[Category\]:[^\[]*(\s*\[Title\]:[^\[]*)?\s*/i, "").trim();
    }
    s = s.replace(/!\[\s*\]\s*\([^)]*\)/g, "").replace(/\s+/g, " ").trim();
    return s || text;
}

/**
 * Traditional search results list.
 * Figma node 139-2384: "{N} results" header, divider, breadcrumb trail, blue
 * title links (#0c3e9d), description with bold keyword highlighting (#2d2d2d).
 */
export default function SearchResultsList({ results = [], query = "" }) {
    const navigate = useNavigate();

    if (!results.length) return null;

    const getArticlePath = (article) => {
        const slug = article.slug || "";
        return slug ? `/${slug}` : null;
    };

    return (
        <Box>
            {/* ── Result count header + divider ── */}
            <Box sx={{ px: "4px" }}>
                <Typography
                    sx={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: "20px",
                        color: "#2d2d2d",
                    }}
                >
                    {results.length} results
                </Typography>
            </Box>
            <Divider sx={{ mt: 1, mb: 0, borderColor: "rgba(0,0,0,0.08)" }} />

            {/* ── Result items ── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "32px", mt: "20px" }}>
                {results.map((article) => {
                    const path = getArticlePath(article);
                    return (
                        <Box key={article.id ?? article.slug ?? article.title}>
                            <Box
                                onClick={() => path && navigate(path)}
                                role={path ? "link" : undefined}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    cursor: path ? "pointer" : "default",
                                    "&:hover .result-title": path
                                        ? { textDecoration: "underline" }
                                        : {},
                                }}
                            >
                                {/* Breadcrumb trail */}
                                <Box sx={{ px: "4px" }} onClick={(e) => e.stopPropagation()}>
                                    <BreadcrumbTrail breadcrumb={article.breadcrumb} slug={article.slug} />
                                </Box>

                                {/* Title — blue link */}
                                <Box sx={{ px: "4px" }}>
                                    <Typography
                                        className="result-title"
                                        sx={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontWeight: 400,
                                            fontSize: 16,
                                            lineHeight: "24px",
                                            color: "#0c3e9d",
                                        }}
                                    >
                                        {article.title}
                                    </Typography>
                                </Box>

                                {/* Content snippet only (no category/title); 1 line, keyword highlighted */}
                                <Box sx={{ px: "4px" }}>
                                    <Typography
                                        component="p"
                                        sx={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontWeight: 400,
                                            fontSize: 16,
                                            lineHeight: "24px",
                                            color: "#525252",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            maxWidth: "960px",
                                        }}
                                    >
                                        {highlightQuery(
                                            getSnippetOnly(article.description ?? article.content ?? ""),
                                            query
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

/**
 * Breadcrumb trail with > arrows; each segment is clickable (Home → /, category → /slug, last → article).
 * slug e.g. "getting-started/account-setup" gives paths for each segment.
 */
function BreadcrumbTrail({ breadcrumb, slug }) {
    const navigate = useNavigate();
    if (!breadcrumb) return null;

    const segments = breadcrumb.split(/\s*[>›]\s*/).filter(Boolean);
    const pathParts = (slug || "").split("/").filter(Boolean);

    const getPathForIndex = (idx) => {
        if (idx < pathParts.length) return "/" + pathParts.slice(0, idx + 1).join("/");
        return null;
    };

    const handleSegmentClick = (path) => {
        if (path) navigate(path);
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {segments.map((segment, idx) => {
                const path = getPathForIndex(idx);
                const clickable = !!path;
                return (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {idx > 0 && (
                            <ChevronRightIcon sx={{ fontSize: 18, color: "#898989" }} />
                        )}
                        <Typography
                            component="span"
                            onClick={() => handleSegmentClick(path)}
                            sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 400,
                                fontSize: 14,
                                lineHeight: "20px",
                                color: "#898989",
                                cursor: clickable ? "pointer" : "default",
                                "&:hover": clickable ? { textDecoration: "underline" } : {},
                            }}
                        >
                            {segment}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

/**
 * Highlights query words in text with bold + darker color.
 * Per Figma: bold keywords use font-weight medium and color #2d2d2d.
 */
function highlightQuery(text, query) {
    if (!query || !text) return text;
    const words = query
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 2);
    if (!words.length) return text;

    const regex = new RegExp(`(${words.join("|")})`, "gi");
    const parts = text.split(regex);
    // Odd-indexed parts are the regex matches (Figma: font-weight 500, color #2d2d2d)
    return parts.map((part, i) =>
        i % 2 === 1 && part ? (
            <Box
                key={i}
                component="span"
                sx={{ fontWeight: 500, color: "#2d2d2d" }}
            >
                {part}
            </Box>
        ) : (
            part
        )
    );
}
